import React, { useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

import {
  useFillsPaginatedQuery,
  PAGE_LIMIT,
} from '#/api/queries/fillPaginatedQueries';
import Checkbox from '#/components/basic/inputs/Checkbox';
import ToggleSlider from '#/components/basic/inputs/ToggleSlider/ToggleSlider';
import { DebugPanel } from '#/components/dev/DebugPanel';
import MetaTags from '#/components/layout/header/MetaTags';
import { MarketSearchDropdown } from '#/components/markets/MarketSearchDropdown';
import OrderDetailScreen from '#/components/orders/detail/OrderDetailScreen';
import DownloadTradesCSV from '#/components/trading/MyTrades/DownloadTradesCSV';
import MyTradeHistoryMultiMarket from '#/components/trading/MyTrades/MyTradeHistoryMultiMarket';
import WalletDashboardContent from '#/components/wallet/dashboard/WalletDashboardContent';
import { useNavigateToPrevious } from '#/lib/redirect';
import { setTradeHistoryAggregateFills } from '#/reduxState/exchange/userGuideReducer';
import { staticDispatch, useSelector } from '#/reduxState/store';

const Flex = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
`;

/**
 * Mobile header renders this slot before Deposit / Withdraw buttons which
 * stretch over the row - move the toggle after them and push it to the
 * right edge (CSV download stays first)
 */
const MobileAggregateToggle = styled.div`
  order: 1;
  margin-left: auto;
`;

function TradeHistoryPage() {
  const isMobile = useSelector(state => state.screen.isMobile);
  const navigateToPrevious = useNavigateToPrevious('/wallet/history/trades');
  const [searchMarket, setSearchMarket] = useState('');
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  // Persisted user preference (local storage)
  const aggregateByOrder = useSelector(
    state => state.exchange.userGuide.tradeHistoryAggregateFills
  );
  // Dev only (DebugPanel) - column hidden by default
  const [showOrderIdColumn, setShowOrderIdColumn] = useState(false);

  const [page, setPage] = useState(1);

  const {
    data,
    isLoading,
    isPlaceholderData: isPreviousData,
  } = useFillsPaginatedQuery({
    page,
    searchMarket,
    walletAddress,
    paginateByExecutions: aggregateByOrder,
  });

  const fills = data?.items;

  const onChangeSearchMarket = (market: string) => {
    setPage(1);
    setSearchMarket(market);
  };

  // TODO: is this safe ? maybe we can check if it's format of correct ID (or length at min)
  const { orderUuid: activeDetailOrderUuid, fillId: activeDetailOrderFillId } =
    useParams<{ orderUuid?: string; fillId?: string }>();

  const detailScreen = activeDetailOrderUuid ? (
    <OrderDetailScreen
      orderUuid={activeDetailOrderUuid}
      highlightedFillId={activeDetailOrderFillId}
      showingOn="trade-history"
      onClose={navigateToPrevious}
    />
  ) : null;

  const disabledControls = !fills?.length && !searchMarket;

  const aggregateToggle = (
    <ToggleSlider
      checked={aggregateByOrder}
      onChange={aggregate => {
        // Pages are counted differently (executions vs fills)
        setPage(1);
        staticDispatch(setTradeHistoryAggregateFills(aggregate));
      }}
      disabled={disabledControls}
    >
      Aggregate
    </ToggleSlider>
  );

  return isMobile && detailScreen ? (
    <>{detailScreen}</>
  ) : (
    <>
      {!isMobile && detailScreen && <>{detailScreen}</>}

      <MetaTags title="Trade History" />

      <DebugPanel fixedBottomRight desktopOnly bottomMargin={24}>
        <Checkbox
          checked={showOrderIdColumn}
          onChange={setShowOrderIdColumn}
          inline
        >
          Order ID &amp; Sequence columns
        </Checkbox>
      </DebugPanel>

      <WalletDashboardContent
        title="Trade History"
        showBalanceActions
        headerContentRight={
          isMobile ? (
            <>
              <DownloadTradesCSV disabled={disabledControls} />
              <MobileAggregateToggle>{aggregateToggle}</MobileAggregateToggle>
            </>
          ) : (
            <Flex>
              <div style={{ padding: '0 16px 0 12px' }}>
                <DownloadTradesCSV disabled={disabledControls} />
              </div>
              <div style={{ padding: '0 16px 0 0' }}>{aggregateToggle}</div>
              <MarketSearchDropdown
                selectedMarket={searchMarket}
                onSelect={onChangeSearchMarket}
                disabled={disabledControls}
              />
            </Flex>
          )
        }
        headerContentMiddle={
          isMobile ? (
            <div style={{ padding: '0 0 12px 0' }}>
              <MarketSearchDropdown
                selectedMarket={searchMarket}
                onSelect={onChangeSearchMarket}
                disabled={disabledControls}
              />
            </div>
          ) : undefined
        }
      >
        <MyTradeHistoryMultiMarket
          fills={fills || []}
          onChangePage={setPage}
          page={page}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
          totalCount={data?.count}
          pageLimit={PAGE_LIMIT}
          aggregateByOrder={aggregateByOrder}
          showOrderId={showOrderIdColumn}
        />
      </WalletDashboardContent>
    </>
  );
}

export default TradeHistoryPage;
